/**
 * The status icons of the queue (round 2 of the minimal redesign, 12 Oct 2026).
 *
 * One icon per meaning, the same one the case page uses for that state, so a sales
 * person learns the icon once: the Action column puts it in front of the action word,
 * the status tabs carry theirs next to the label. "Reading" has no icon here because
 * the row keeps the DS Spinner while the engine works.
 *
 * The maps live in lib/line-icons.ts with the rest of the legend; this file only widens
 * them to string keys, because the queue looks an icon up by the action word or the tab
 * label it already holds as a string, and handles the "Pick n" prefix.
 */

import { type IconComponent } from "@norican/icons";

import { ACTION_WORD } from "@/features/queue/action-word";
import {
  CUSTOMER_ICON,
  QUEUE_ICON as SHARED_QUEUE_ICON,
  TAB_ICON as SHARED_TAB_ICON,
} from "@/lib/line-icons";

/**
 * The icon in front of each action word; "Pick n" is matched by its prefix below.
 * "Pick the customer" (round 5) carries the suggested-customer icon of the case page's
 * customer line: the account is suggested, a person settles it there.
 */
export const QUEUE_ICON: Readonly<Record<string, IconComponent>> = {
  ...SHARED_QUEUE_ICON,
  [ACTION_WORD.pickCustomer]: CUSTOMER_ICON.suggested,
};

/**
 * The icon next to the tab labels "To review" and "Closed". "Ready to quote" (a primary
 * tab since 13 Oct 2026) and the views behind More carry none.
 */
export const TAB_ICON: Readonly<Record<string, IconComponent>> = SHARED_TAB_ICON;

/**
 * The icon of an action word, or null for "Reading" (the Spinner stands in) and for
 * a word the map does not know, so an unexpected word still renders as plain text.
 */
export function queueActionIcon(word: string): IconComponent | null {
  if (word === ACTION_WORD.reading) {
    return null;
  }
  // A word of the map first ("Pick the customer" is not a count); "Pick 3" carries the
  // count, and the icon is the same for every count.
  const exact = QUEUE_ICON[word];
  if (exact) {
    return exact;
  }
  return word.startsWith("Pick ") ? (QUEUE_ICON.Pick ?? null) : null;
}
